export function openChatConversationId(pathname: string) {
  const match = pathname.match(/^\/chat\/([^/]+)$/);
  return match?.[1] ?? null;
}

export const CHAT_ATTACHMENT_BUCKET = "chat-attachments";
export const CONVERSATION_MEDIA_BUCKET = "conversation-media";
export const MAX_CHAT_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_CONVERSATION_MEDIA_BYTES = 8 * 1024 * 1024;
export const MESSAGE_PAGE_SIZE = 40;

export const CHAT_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const CHAT_DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

export const CHAT_ATTACHMENT_TYPES = [
  ...CHAT_IMAGE_TYPES,
  ...CHAT_DOCUMENT_TYPES,
] as const;

export const CONVERSATION_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export type ConversationType = "direct" | "group" | "community";
export type ConversationMemberRole = "member" | "admin" | "owner";
export type ChatMessageType = "text" | "image" | "file" | "link" | "system";

export type ChatUser = {
  id: string;
  fullName: string;
  professionalTitle: string | null;
  avatarPath: string | null;
  practiceName: string | null;
};

export type ChatMember = {
  userId: string;
  role: ConversationMemberRole;
  fullName: string;
  professionalTitle: string | null;
  avatarPath: string | null;
};

export type ChatAttachment = {
  bucket: string;
  path: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  url: string | null;
};

export type ChatMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  messageType: ChatMessageType;
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  attachment: ChatAttachment | null;
  replyTo: {
    id: string;
    senderName: string;
    body: string | null;
    deleted: boolean;
  } | null;
};

export type ChatConversation = {
  id: string;
  type: ConversationType;
  title: string | null;
  avatarPath: string | null;
  avatarUrl: string | null;
  updatedAt: string;
  myRole: ConversationMemberRole;
  lastReadAt: string | null;
  unreadCount: number;
  lastMessage: {
    id: string;
    body: string | null;
    type: ChatMessageType;
    createdAt: string;
    senderId: string;
    deleted: boolean;
  } | null;
  members: ChatMember[];
  displayName: string;
  canManage: boolean;
  allowsVideo: boolean;
};

export type ConversationCallStatus = "ringing" | "active" | "ended";

export type ConversationCall = {
  id: string;
  conversationId: string;
  createdBy: string;
  dailyRoomName: string;
  dailyRoomUrl: string;
  status: ConversationCallStatus;
  createdAt: string;
};

export function isServiceConversation(
  conversation: Pick<ChatConversation, "allowsVideo">,
) {
  return conversation.allowsVideo;
}

export function isChatImageType(value: string) {
  return CHAT_IMAGE_TYPES.includes(value as (typeof CHAT_IMAGE_TYPES)[number]);
}

export function isChatAttachmentType(value: string) {
  return CHAT_ATTACHMENT_TYPES.includes(
    value as (typeof CHAT_ATTACHMENT_TYPES)[number],
  );
}

export function previewForMessage(input: {
  body: string | null;
  type: ChatMessageType;
  deleted: boolean;
}) {
  if (input.deleted) {
    return "Az üzenetet törölték.";
  }
  if (input.type === "image") {
    return "Kép";
  }
  if (input.type === "file") {
    return "Dokumentum";
  }
  return input.body?.trim() || "Új üzenet";
}
